import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSession, login as loginRequest, logout as logoutRequest, type SessionInfo } from '../../api/authApi.js';
import { ApiError } from '../../api/client.js';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  source?: SessionInfo['source'];
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [source, setSource] = useState<SessionInfo['source'] | undefined>(undefined);

  const sessionQuery = useQuery<SessionInfo, ApiError>({
    queryKey: ['auth', 'session'],
    queryFn: ({ signal }) => fetchSession({ signal }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (sessionQuery.isSuccess) {
      setStatus('authenticated');
      setSource(sessionQuery.data.source);
      return;
    }

    if (sessionQuery.isError) {
      if (sessionQuery.error.status === 401) {
        setStatus('unauthenticated');
        setSource(undefined);
      } else {
        console.error('Failed to resolve auth session', sessionQuery.error);
        setStatus('unauthenticated');
        setSource(undefined);
      }
      return;
    }

    setStatus('loading');
    setSource(undefined);
  }, [sessionQuery.isSuccess, sessionQuery.isError, sessionQuery.data, sessionQuery.error]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
  }, [queryClient]);

  const login = useCallback(
    async (password: string) => {
      await loginRequest(password);
      setStatus('authenticated');
      setSource('session');
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    setStatus('unauthenticated');
    setSource(undefined);
    await refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    source,
    login,
    logout,
    refresh,
  }), [status, source, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
